import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type {
  MaintenanceOverview,
  PartStatus,
  Repair,
  RepairPriority,
  RepairStatus,
  ShoppingItem,
  VehicleCheckView,
} from '../types';

export function useMaintenanceOverview() {
  return useQuery({
    queryKey: qk.maintenance.overview(),
    queryFn: () => api.get<MaintenanceOverview>('/maintenance/overview'),
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

export function useVehicleChecks(filter: 'new' | 'problems' | 'all', vehicleId?: string) {
  return useQuery({
    queryKey: qk.maintenance.checks(filter, vehicleId),
    queryFn: () =>
      api.get<VehicleCheckView[]>('/maintenance/checks', { filter, ...(vehicleId ? { vehicleId } : {}), limit: 100 }),
    staleTime: 20_000,
  });
}

export function useRepairs(status: 'open' | 'done' | 'all') {
  return useQuery({
    queryKey: qk.maintenance.repairs(status),
    queryFn: () => api.get<Repair[]>('/maintenance/repairs', { status }),
    staleTime: 20_000,
  });
}

export function useRepair(id: string | undefined) {
  return useQuery({
    queryKey: qk.maintenance.repair(id ?? 'none'),
    queryFn: () => api.get<Repair>(`/maintenance/repairs/${id}`),
    enabled: Boolean(id),
  });
}

export function useShoppingList() {
  return useQuery({
    queryKey: qk.maintenance.shopping(),
    queryFn: () =>
      api.get<{ items: ShoppingItem[]; totals: Array<{ ccy: string; amount: number }> }>('/maintenance/shopping-list'),
    staleTime: 20_000,
  });
}

/** Всяка промяна в сервиза опреснява всичко в него — екраните са малки. */
function useMaintenanceMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.maintenance.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
  });
}

export interface ServiceConflict {
  tripId: string;
  number: string;
  status: string;
  plannedStartAt: string | null;
}

export function useSetService() {
  return useMaintenanceMutation((input: { vehicleId: string; inService: boolean; reason?: string; until?: string | null }) =>
    api.post<{ vehicleId: string; inService: boolean; conflicts: ServiceConflict[] }>(
      `/maintenance/vehicles/${input.vehicleId}/service`,
      { inService: input.inService, ...(input.reason ? { reason: input.reason } : {}), ...(input.until ? { until: input.until } : {}) },
    ),
  );
}

export function useMechanicCheck() {
  return useMaintenanceMutation(
    (input: { vehicleId: string; items: Array<{ key: string; ok: boolean }>; odometerKm?: number; problem?: string }) =>
      api.post<{ id: string }>(`/maintenance/vehicles/${input.vehicleId}/checks`, {
        items: input.items,
        ...(input.odometerKm !== undefined ? { odometerKm: input.odometerKm } : {}),
        ...(input.problem ? { problem: input.problem } : {}),
      }),
  );
}

export function useResolveCheck() {
  return useMaintenanceMutation((input: { checkId: string; resolution: string }) =>
    api.post<{ id: string }>(`/maintenance/checks/${input.checkId}/resolve`, { resolution: input.resolution }),
  );
}

export function useCreateRepair() {
  return useMaintenanceMutation(
    (input: { vehicleId: string; title: string; description?: string; priority: RepairPriority; sourceCheckId?: string }) =>
      api.post<Repair>('/maintenance/repairs', input),
  );
}

export function useUpdateRepair() {
  return useMaintenanceMutation(
    (input: {
      id: string;
      title?: string;
      description?: string | null;
      status?: RepairStatus;
      priority?: RepairPriority;
      notes?: string | null;
    }) => {
      const { id, ...body } = input;
      return api.patch<Repair>(`/maintenance/repairs/${id}`, body);
    },
  );
}

export function useAddPart() {
  return useMaintenanceMutation(
    (input: {
      repairId: string;
      name: string;
      partNumber?: string | null;
      quantity: number;
      unitPrice?: number | null;
      ccy?: string;
      supplier?: string | null;
    }) => {
      const { repairId, ...body } = input;
      return api.post<Repair>(`/maintenance/repairs/${repairId}/parts`, body);
    },
  );
}

export function useUpdatePart() {
  return useMaintenanceMutation(
    (input: {
      id: string;
      status?: PartStatus;
      unitPrice?: number | null;
      quantity?: number;
      supplier?: string | null;
      ccy?: string;
    }) => {
      const { id, ...body } = input;
      return api.patch<Repair>(`/maintenance/parts/${id}`, body);
    },
  );
}

export function useDeletePart() {
  return useMaintenanceMutation((id: string) => api.del<Repair>(`/maintenance/parts/${id}`));
}
